import { Graphics, GraphicsContext, type Container } from 'pixi.js';
import { buildingType } from '../data/buildings.ts';
import { harvestKind, nodeKind } from '../data/nodes.ts';
import { TICK_DT, TICK_RATE } from '../sim/constants.ts';
import { DAY_SECONDS } from '../sim/systems/calendar.ts';
import { productionThrottle } from '../sim/systems/settlement.ts';
import { alliedWith, Job, Phase, RESOURCE_KEYS, UF, UnitState, type ResourceKey } from '../sim/types.ts';
import type { World } from '../sim/world.ts';
import { gait, UNIT_WORK_FRAMES } from './unitmotion.ts';

export type WorkTool = 'hammer' | 'axe' | 'pick' | 'hoe' | 'saw' | 'peel';
export type WorkEffect = 'chips' | 'dust' | 'sparks';
export interface WorkerActivity {
  tool: WorkTool | null;
  cargo: ResourceKey | null;
  effect: WorkEffect | null;
  /** A real work target, in map tiles; absent for a delivery in transit. */
  target: { x: number; y: number } | null;
}

/** Unlike the command-log tick, the calendar also stops during modal events. */
export function workTick(w: World): number { return (w.calendarDay * DAY_SECONDS + w.calendarT) * TICK_RATE; }

/** Pass workTick(w), so pausing also pauses the hammer. */
export function workFrame(tick: number, unit: number): number {
  return Math.floor(tick / TICK_RATE * 5.2 + unit * .71) % UNIT_WORK_FRAMES;
}

/** Read actual assignments and loads. Standing at an empty camp is not cutting. */
export function workerActivity(w: World, i: number): WorkerActivity | null {
  const u = w.units;
  if (i < 0 || i >= u.slots.high || !u.slots.alive[i] || !(u.flags[i] & UF.Villager) ||
      u.hp[i] <= 0 || u.state[i] === UnitState.Dying || u.state[i] === UnitState.Routing ||
      u.state[i] === UnitState.Attacking || w.battle) return null;
  const cargo = u.carry[i] > 1e-4 && u.carryKind[i] > 0 ? RESOURCE_KEYS[u.carryKind[i] - 1] ?? null : null;
  const result: WorkerActivity = { tool: null, cargo, effect: null, target: null };
  const idle = () => cargo ? result : null;
  if (u.state[i] !== UnitState.Working || u.stunT[i] > 0 || w.config.villageType) return idle();
  const b = w.buildings, site = u.workSite[i];
  if (site < 0 || site >= b.slots.high || !b.slots.alive[site] || b.hp[site] <= 0 ||
      !alliedWith(u.team[i], b.team[site])) return idle();
  const def = buildingType(b.typeId[site]);
  const cx = b.tx[site] + def.w / 2, cy = b.ty[site] + def.h / 2;
  const atSite = (u.x[i] - cx) ** 2 + (u.y[i] - cy) ** 2 <= (Math.max(def.w, def.h) / 2 + 1.6) ** 2;
  if (u.job[i] === Job.Builder) {
    if (!atSite || (b.build[site] >= 1 && b.hp[site] >= b.maxHp[site])) return idle();
    if (b.build[site] >= 1 && w.phase === Phase.Siege && !w.repairOrders.includes(site)) return idle();
    return { ...result, tool: 'hammer', effect: 'dust', target: { x: cx, y: cy } };
  }
  if (b.build[site] < 1 || u.job[i] !== def.job) return idle();
  const harvest = harvestKind(u.job[i]);
  if (harvest >= 0) {
    const n = u.nodeTarget[i], nd = nodeKind(harvest);
    if (n < 0 || !w.nodes.isAlive(n) || w.nodes.kind[n] !== harvest || w.nodes.amount[n] <= 0 ||
        u.carry[i] >= nd.load - 1e-4) return idle();
    const tile = w.nodes.tile[n], x = tile % w.map.w + .5, y = Math.floor(tile / w.map.w) + .5;
    // Settlement work reach is measured from a cardinal tile outside the
    // footprint, not from the node centre. In particular a woodcutter can be
    // 1 + 1.8 tiles from a tree while genuinely increasing his carried load.
    // Bound that work-tile envelope without running harvest pathfinding here.
    const radius = Math.floor((w.nodes.footprint[n] || 1) / 2);
    const reach = Math.hypot(radius + 1, radius) + (nd.footprint > 1 ? .65 : 1.8) + .02;
    if ((u.x[i] - x) ** 2 + (u.y[i] - y) ** 2 > reach ** 2) return idle();
    return { ...result, tool: u.job[i] === Job.Woodcutter ? 'axe' : 'pick',
      effect: u.job[i] === Job.Woodcutter ? 'chips' : 'dust', target: { x, y } };
  }
  if (!atSite) return idle();
  const tool: WorkTool | null = u.job[i] === Job.Farmer ? 'hoe' : u.job[i] === Job.Smith ? 'hammer'
    : u.job[i] === Job.Sawyer ? 'saw' : u.job[i] === Job.Baker ? 'peel' : null;
  if (!tool) return idle();
  // A worker can remain assigned while inputs run out or a converter's store
  // fills. Do not advertise activity at those stalled workshops.
  const staffing = def.workers > 0 ? b.workers[site] / def.workers : 1;
  const tier = 1 + (b.tier[site] - 1) * .45;
  // Read the sim's existing district multiplier; asking districtBonus would
  // lazily mutate the world's derived caches from the renderer.
  if (staffing <= 0 || productionThrottle(w,def,staffing,tier,w.districts.mul[site] || 1,TICK_DT).throttle <= 0) return idle();
  return { ...result, tool, effect: u.job[i] === Job.Smith ? 'sparks'
    : u.job[i] === Job.Sawyer ? 'chips' : null, target: { x: cx, y: cy } };
}

export interface WorkerPose {
  /** Ground anchor and sort order shared with the unit body. */
  x: number;
  y: number;
  depth: number;
  /** Baked hand offset in authoring pixels, relative to that anchor. */
  handX: number;
  handY: number;
  scale: number;
  direction: number;
  frame: number;
  /** False while interpolating movement, even if the last sim state was Working. */
  working: boolean;
}

type Point = [number, number];
function project(direction: number, x: number, y: number, z: number): Point {
  const a = direction * Math.PI / 4, c = Math.cos(a), s = Math.sin(a);
  // The same upright 2:1 ground projection as the baked worker mesh.
  return [(x * c - y * s) * .89, ((x * s + y * c) * .5 - z * .92) * .89];
}

function toolArt(g: GraphicsContext, tool: WorkTool, direction: number, frame: number): void {
  const bend = gait(frame, 'work').bend;
  const a = project(direction, -2.4, 0, -1.6), tip = project(direction, 6.5 - bend * 2, 0, 4.5 - bend * 9);
  g.moveTo(...a).lineTo(...tip).stroke({ color: 0x413321, width: 1.7, cap: 'round' });
  g.moveTo(a[0] - .2, a[1] - .2).lineTo(tip[0] - .2, tip[1] - .2)
    .stroke({ color: 0xa58a56, width: .8, cap: 'round' });
  const metal = 0xa5aca4, dark = 0x454f4d;
  const head = (points: number[], color = metal) => g.poly(points).fill(color).stroke({ color: dark, width: .55 });
  const [x, y] = tip;
  if (tool === 'hammer') {
    head([x - 3.1, y - 1.6, x + 2.4, y - 2.1, x + 3.1, y + .8, x - 2.5, y + 1.3]);
    g.moveTo(x - 2.7, y - 1.5).lineTo(x + 2.1, y - 2).stroke({ color: 0xd0d2be, width: .6 });
  } else if (tool === 'axe') {
    head([x - .5, y - 1.5, x + 3.4, y - 3.6, x + 4.5, y + 1.2, x, y + .8]);
    g.moveTo(x + 3.5, y - 3.3).lineTo(x + 4.3, y + .9).stroke({ color: 0xe1dfca, width: .7 });
  } else if (tool === 'pick') {
    head([x - 5, y + 1.8, x - 2.4, y - 1.1, x + 2.5, y - 1.3, x + 5.1, y + 1.1, x + 1.6, y, x - 1.6, y + .1]);
  } else if (tool === 'hoe') {
    head([x - .7, y - 1.3, x + 3.8, y + .3, x + 2.9, y + 3.8, x - 1.5, y + 1.8]);
  } else if (tool === 'saw') {
    head([x - 6, y - 1.3, x + 3.9, y - 2.3, x + 4.4, y + .1, x + 2.9, y + .2,
      x + 2.4, y + 1.3, x + 1.5, y + .4, x + .5, y + 1.6, x - .5, y + .7, x - 2, y + 1.7, x - 3.3, y + .9]);
  } else {
    head([x - 2, y - 2.2, x + 2.2, y - 2.5, x + 3.1, y + .6, x - 1.2, y + 1.3], 0xb3996b);
  }
}

type CargoArt = 'logs' | 'planks' | 'stone' | 'iron' | 'sack' | 'crate';
function cargoArtKind(cargo: ResourceKey): CargoArt {
  if (cargo === 'timber' || cargo === 'magewood' || cargo === 'soulwood') return 'logs';
  if (cargo === 'planks') return 'planks';
  if (cargo === 'stone' || cargo === 'runestone' || cargo === 'voidstone') return 'stone';
  if (cargo === 'iron' || cargo === 'ingots' || cargo === 'steel' || cargo === 'heartiron' || cargo === 'scrap') return 'iron';
  if (cargo === 'grain' || cargo === 'bread' || cargo === 'fish' || cargo === 'meat' || cargo === 'charcoal') return 'sack';
  return 'crate';
}

function cargoArt(g: GraphicsContext, cargo: CargoArt, direction: number): void {
  const [x, y] = project(direction, -1.4, 5, 12.4);
  if (cargo === 'logs' || cargo === 'planks') {
    for (let j = 0; j < 3; j++) {
      const yy = y + j * 1.7;
      g.moveTo(x - 5, yy + 1).lineTo(x + 5, yy - 2).stroke({ color: j === 1 ? 0x725031 : 0x87643b, width: 2.3, cap: cargo === 'logs' ? 'round' : 'butt' });
      g.moveTo(x - 4.5, yy + .4).lineTo(x + 4.7, yy - 2.6).stroke({ color: 0xaa8551, width: .6 });
      if (cargo === 'logs') g.ellipse(x + 5, yy - 2, 1.1, 1.2).fill(0xd2b67b).stroke({ color: 0x74532f, width: .45 });
    }
    g.moveTo(x - 1.2, y -.2).lineTo(x - .7, y + 4.4).stroke({ color: 0xc1b693, width: .8 });
    return;
  }
  if (cargo === 'sack') {
    g.poly([x - 2.2,y - 4,x + 1.4,y - 4,x + 3.8,y - .5,x + 3.4,y + 4.1,x - 2.8,y + 4.8,x - 4.1,y + 1])
      .fill(0xb49d6c).stroke({ color: 0x5c5137, width: .65 });
    g.moveTo(x - 1.6,y - 3.5).lineTo(x + 1.3,y - 3.5).stroke({ color: 0x5c5137, width: .8 });
    g.moveTo(x - 2.6,y - .7).lineTo(x - 1.6,y + 3.4).stroke({ color: 0xd5c195, width: .8 });
    return;
  }
  g.poly([x - 4,y - 1.7,x + 3.6,y - 2.2,x + 2.8,y + 4.1,x - 3.2,y + 4.4])
    .fill(0x816545).stroke({ color: 0x433b2d, width: .7 });
  if (cargo !== 'crate') {
    const rocks = cargo === 'iron' ? [0x695f53,0x929582,0x87715b] : [0x8e9484,0xb6b8a2,0x727f78];
    for (let j = 0; j < 3; j++) {
      const xx = x - 2.5 + j * 2.3, yy = y - (j === 1 ? 2.9 : 1.2);
      g.poly([xx - 1.4,yy + .8,xx - 1.1,yy - 1.3,xx + .7,yy - 2,xx + 1.7,yy + .7])
        .fill(rocks[j]).stroke({ color: 0x4d5046, width: .4 });
    }
  }
  for (const dy of [0,1.5,3]) g.moveTo(x - 3.5,y + dy).lineTo(x + 3.1,y + dy - .4).stroke({ color: 0xb09968, width: .55 });
  g.moveTo(x - 3,y - 1).quadraticCurveTo(x - 1,y - 5,x + 2.4,y - 1.5).stroke({ color: 0xab9470, width: .7 });
}

function effectArt(g: GraphicsContext, kind: WorkEffect, direction: number, frame: number): void {
  if (frame !== 3 && frame !== 4) return;
  const spread = frame === 3 ? 1 : 1.8;
  const [x, y] = project(direction, 4.5, 0, -4.5);
  for (let j = 0; j < 3; j++) {
    const dx = [-3,1,4][j] * spread, dy = [-2,-4,-1][j] * spread;
    if (kind === 'sparks') {
      g.moveTo(x + dx,y + dy).lineTo(x + dx * 1.2,y + dy - 1.1)
        .stroke({ color: j === 1 ? 0xf0c46f : 0xc69255, width: .65 });
    } else if (kind === 'chips') {
      g.poly([x + dx,y + dy,x + dx + 1.4,y + dy -.4,x + dx + 1,y + dy + .6]).fill(0xb69864);
    } else g.ellipse(x + dx,y + dy,1.4,.8).fill({ color: 0xc2b69b, alpha: .35 });
  }
}

type Workpiece = 'trestle' | 'anvil' | 'block' | 'rubble';
function workpieceArt(g: GraphicsContext, kind: Workpiece, direction: number): void {
  // The shared work pose contacts local (13.5, 1.2, 4.5). Keep the working
  // surface there: these are loose materials on the ground beside the worker,
  // not an invented permanent structure or a change to his sim position.
  const p = (x: number, y: number, z: number) => project(direction,x + 13.5,y + 1.2,z);
  const box = (x: number, y: number, z: number, width: number, depth: number, height: number, color: number) => {
    const base = [[x - width / 2,y - depth / 2],[x + width / 2,y - depth / 2],
      [x + width / 2,y + depth / 2],[x - width / 2,y + depth / 2]];
    const angle = direction * Math.PI / 4;
    for (let j = 0; j < 4; j++) {
      const a = base[j], b = base[(j + 1) % 4];
      if ((b[1] - a[1]) * Math.sin(angle) + (a[0] - b[0]) * Math.cos(angle) <= 0) continue;
      g.poly([...p(a[0],a[1],z),...p(b[0],b[1],z),...p(b[0],b[1],z + height),...p(a[0],a[1],z + height)])
        .fill(color).stroke({ color: 0x403b2c, width: .35 });
    }
    g.poly(base.flatMap(([xx,yy]) => p(xx,yy,z + height))).fill(color)
      .stroke({ color: kind === 'anvil' ? 0xb2b5a2 : 0xb59c6b, width: .45 });
  };
  const anchor = p(0,0,0);
  g.ellipse(anchor[0],anchor[1] + .4,4.6,2.2).fill({ color: 0x302b21, alpha: .2 });
  if (kind === 'trestle') {
    for (const x of [-2.6,2.6]) {
      for (const y of [-1.6,1.6]) box(x,y,0,.9,.9,3.3,0x6e573a);
      box(x,0,2.8,1,4,.9,0x8a7048);
    }
    box(0,0,3.6,8,2.4,.9,0xa48450);
    const a = p(-3.5,-.35,4.53), b = p(3.5,-.35,4.53);
    g.moveTo(...a).lineTo(...b).stroke({ color: 0x685234, width: .4 });
  } else if (kind === 'anvil') {
    box(0,0,0,4.8,3.7,2,0x6a5436);
    box(0,0,2,2.1,2,1.5,0x646c65);
    box(0,0,3.5,6.3,2.7,1,0x838b7f);
    g.poly([...p(3.15,-1,3.6),...p(5,0,4),...p(3.15,1,4.45)]).fill(0x929b8c);
  } else if (kind === 'block') {
    box(0,0,0,4.4,4,3.2,0x775432);
    box(0,0,3.2,7,2.3,1.3,0xa18451);
    const cut = [p(3.55,-1.1,3.2),p(3.55,1.1,3.2),p(3.55,1.1,4.5),p(3.55,-1.1,4.5)].flat();
    g.poly(cut).fill(0xcbb17b);
  } else {
    g.poly([...p(-3,-1,0),...p(2.4,-1.8,0),...p(3.5,1.8,0),...p(-2,2,0)])
      .fill(0x777f72).stroke({ color: 0x4b5149, width: .45 });
    g.poly([...p(-3,-1,0),...p(-1.3,-.8,3.8),...p(1.4,.7,4.5),...p(3.5,1.8,0),...p(-2,2,0)])
      .fill(0xa3a78f).stroke({ color: 0x5a6256, width: .45 });
    g.poly([...p(-1.3,-.8,3.8),...p(2.4,-1.8,0),...p(3.5,1.8,0),...p(1.4,.7,4.5)])
      .fill(0x7c887b);
  }
}

/** Reused art sits in the same depth-sorted parent as each worker and building. */
export class WorkActivityLayer {
  private readonly art = new Map<string, GraphicsContext>();
  private readonly pool: Graphics[] = [];
  private used = 0;
  constructor(private readonly parent: Container) {}
  begin(): void { this.used = 0; }

  push(activity: WorkerActivity, pose: WorkerPose, _tick: number): void {
    const direction = pose.direction & 7, frame = pose.frame % UNIT_WORK_FRAMES;
    if (activity.cargo) {
      const cargo = cargoArtKind(activity.cargo);
      this.draw(`cargo/${cargo}/${direction}`, g => cargoArt(g,cargo,direction), pose, false, .12);
    }
    if (!pose.working || !activity.tool) return;
    const piece: Workpiece | null = activity.tool === 'hammer' ? activity.effect === 'sparks' ? 'anvil' : 'trestle'
      : activity.tool === 'axe' ? 'block' : activity.tool === 'pick' ? 'rubble' : activity.tool === 'saw' ? 'trestle' : null;
    if (piece) this.draw(`piece/${piece}/${direction}`, g => workpieceArt(g,piece,direction), pose, false, .08);
    this.draw(`tool/${activity.tool}/${direction}/${frame}`, g => toolArt(g,activity.tool!,direction,frame), pose, true, .14);
    if (activity.effect && (frame === 3 || frame === 4)) {
      this.draw(`effect/${activity.effect}/${direction}/${frame}`, g => effectArt(g,activity.effect!,direction,frame), pose, true, .16);
    }
  }

  private draw(key: string, build: (g: GraphicsContext) => void, pose: WorkerPose, hand: boolean, depth: number): void {
    let context = this.art.get(key);
    if (!context) { context = new GraphicsContext(); build(context); this.art.set(key,context); }
    let g = this.pool[this.used];
    if (!g) { g = new Graphics(context); this.pool.push(g); this.parent.addChild(g); }
    this.used++;
    g.context = context;
    g.position.set(pose.x + (hand ? pose.handX * pose.scale : 0), pose.y + (hand ? pose.handY * pose.scale : 0));
    g.scale.set(pose.scale);
    g.zIndex = pose.depth + depth;
    g.visible = true;
  }

  end(): void { for (let j = this.used; j < this.pool.length; j++) this.pool[j].visible = false; }
  destroy(): void {
    for (const g of this.pool) g.destroy({ context: false });
    for (const context of this.art.values()) context.destroy();
    this.pool.length = 0;
    this.art.clear();
  }
}
